import { f7, useStore } from 'framework7-react';
import { useEffect, useRef, useState } from 'react';
import {
  CoatingStep1FormData,
  coatingStep1InitialState,
} from './useCoatingStep1';
import {
  CoatingStep2FormData,
  COATING_CONTACT_METHOD,
} from './useCoatingStep2';
import { CoatingStep3FormData } from './useCoatingStep3';
import {
  createCoating,
  fetchCoatingCourseInfos,
  fetchCoatingUnits,
  paidyConfirmCoating,
  prepareCoatingPayment,
} from '@/api/coatingApi';
import { customerInfoApi } from '@/api/customerApi';
import { postCreateGmoLinkTypePlusCardParameterApi } from '@/api/prepaidApi';
import { SelectedTenant } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { endpoint } from '@/consts/api';
import {
  coatingPaymentMethod,
  CoatingPaymentMethod,
  coatingServiceType,
  coatingSteps,
} from '@/consts/coating';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import { usePaidy } from '@/hooks/usePaidy';
import { useToast } from '@/hooks/useToast';
import {
  CoatingCourseItem,
  CoatingOptionItem,
  CoatingUnitItem,
  CreateCoatingParams,
  FetchCoatingCourseInfosParams,
} from '@/types/api/coatingApi';
import { Customer } from '@/types/api/customerApi';
import { GmoPaymentUrlGenerationParams } from '@/types/api/payment';
import { persistGmoLoginContext } from '@/utils/loginTel';
import { paymentStorage } from '@/utils/paymentStorage';
import { dispatchBasicInfo } from '@/utils/store';

function parseJSON<T = any>(text: string | null): T | null {
  try {
    return JSON.parse(text || '');
  } catch {
    return null;
  }
}

export const useCoating = (status?: string) => {
  const storeCustomerInfo = useStore(store, 'customerInfo') as Customer;
  const selectedTenant = useStore(store, 'getSelectedTenant') as SelectedTenant;
  const { openToast } = useToast();
  const { launchPaidyCheckout } = usePaidy();
  const [step, setStep] = useState<number>(coatingSteps.PRE_SELECTION);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const restoredPayment = useRef<string>();
  const [isGmoProcessing, setIsGmoProcessing] = useState(
    status === 'gmo_success',
  );
  const [courseItems, setCourseItems] = useState<CoatingCourseItem[]>([]);
  const [optionItems, setOptionItems] = useState<CoatingOptionItem[]>([]);
  const [coatingUnits, setCoatingUnits] = useState<CoatingUnitItem[]>([]);
  const [totalFee, setTotalFee] = useState(0);
  const [totalWorkingMinutes, setTotalWorkingMinutes] = useState(0);
  const [step1Data, setStep1Data] = useState<CoatingStep1FormData>(
    coatingStep1InitialState,
  );
  const [step2Data, setStep2Data] = useState<CoatingStep2FormData>({
    preferredDatetimes: {},
    reservationHolder: {},
    mUnitId: undefined,
    unitMemo: '',
    contactMethod: COATING_CONTACT_METHOD.PHONE,
    contactEmail: '',
  });
  const [step3Data, setStep3Data] = useState<CoatingStep3FormData>({
    scratchClaimConsent: false,
    extraChargeConsent: false,
    dateAnnotation: false,
    badWeather: false,
    cancelFee: false,
  });

  const customerId = paymentStorage.getItem('m_customer_id');
  const token =
    paymentStorage.getItem('token') === null
      ? undefined
      : paymentStorage.getItem('token');

  const { retrieveUserInfo } = useLoadCustomer(
    f7.view.main.router,
    token === null ? undefined : token,
    Number(customerId),
  );

  // GMO決済成功でステータス付きで戻ってきた場合
  useEffect(() => {
    if (status === 'gmo_success' || status === 'gmo_cancel') {
      setStep(coatingSteps.REVIEW);
    }
  }, [status]);

  // createCoating API呼び出しの共通処理
  const executeCreateCoating = (createData: CreateCoatingParams) => {
    setSubmitSuccess(true);
    f7.preloader.show();
    createCoating(createData)
      .then(({ data: rs }) => {
        if (!rs.success) {
          if ('error_message' in rs) {
            f7.dialog.alert('', rs.error_message);
          }
          setSubmitSuccess(false);
          return;
        }
        clearCoatingStorage();
        if (storeCustomerInfo.id) {
          customerInfoApi({ m_customer_id: storeCustomerInfo.id }).then(
            ({ data: basicInfo }) => {
              dispatchBasicInfo(basicInfo);
            },
          );
        }
        f7.view.main.router.navigate(paths.carwashEditSuccess('create'));
      })
      .catch(() => {
        openToast(
          '予約の作成に失敗しました。再度お試しください。',
          'toast-failed',
          3000,
          true,
          'top',
        );
        setSubmitSuccess(false);
      })
      .finally(() => {
        f7.preloader.hide();
        setIsGmoProcessing(false);
      });
  };

  // 復帰時の復元は取引ごとに一度だけ。API失敗後は明示的な再試行を待つ。
  useEffect(() => {
    if (
      ['gmo_success', 'gmo_cancel'].includes(status || '') &&
      storeCustomerInfo.id
    ) {
      const tPaymentManagementsId = paymentStorage.getItem(
        'coating_t_payment_managements_id',
      );
      if (
        !tPaymentManagementsId ||
        restoredPayment.current === tPaymentManagementsId
      )
        return;
      const storageData = parseJSON<CreateCoatingParams>(
        paymentStorage.getItem('coating_create_data'),
      );
      // A stale return must never submit another customer's saved reservation.
      if (!storageData || storageData.m_customer_id !== storeCustomerInfo.id)
        return;
      restoredPayment.current = tPaymentManagementsId;

      const storageStep1 = parseJSON<CoatingStep1FormData>(
        paymentStorage.getItem('coating_step_1'),
      );
      const storageStep2 = parseJSON<CoatingStep2FormData>(
        paymentStorage.getItem('coating_step_2'),
      );
      const storageStep3 = parseJSON<CoatingStep3FormData>(
        paymentStorage.getItem('coating_step_3'),
      );
      const storageFee = paymentStorage.getItem('coating_total_fee');

      if (storageStep1) setStep1Data(storageStep1);
      if (storageStep2) setStep2Data(storageStep2);
      if (storageStep3) setStep3Data(storageStep3);
      if (storageFee) setTotalFee(Number(storageFee));
      setTotalWorkingMinutes(
        Number(paymentStorage.getItem('coating_total_working_minutes') || 0),
      );

      if (status === 'gmo_success') {
        const createData: CreateCoatingParams = {
          ...storageData,
          t_payment_managements_id: Number(tPaymentManagementsId),
        };
        executeCreateCoating(createData);
      }
    }
  }, [status, storeCustomerInfo]);

  // GMO決済済み状態での予約作成リトライ（prepareCoatingPaymentを経由しない）
  const retryCreateCoating = () => {
    if (submitSuccess) return;
    const tPaymentManagementsId = paymentStorage.getItem(
      'coating_t_payment_managements_id',
    );
    const storageData = parseJSON<CreateCoatingParams>(
      paymentStorage.getItem('coating_create_data'),
    );
    if (
      !tPaymentManagementsId ||
      !storageData ||
      storageData.m_customer_id !== store.state.authInfo.m_customer_id
    )
      return;
    const createData: CreateCoatingParams = {
      ...storageData,
      t_payment_managements_id: Number(tPaymentManagementsId),
    };
    executeCreateCoating(createData);
  };

  // コース情報取得
  useEffect(() => {
    if (!storeCustomerInfo.id || courseItems.length) return;
    f7.preloader.show();

    // テナント切り替え中の場合、切り替え先のaffiliate_idを使用し、
    // 元のaffiliate_idをoriginal_m_affiliate_idとして渡す
    const originalAffiliateId = store.state.customerInfo.m_affiliate_id;
    const targetAffiliateId =
      selectedTenant?.m_affiliate_id ?? originalAffiliateId;

    const params: FetchCoatingCourseInfosParams = {
      service_type: coatingServiceType,
      m_affiliate_id: targetAffiliateId,
      ...(selectedTenant
        ? { original_m_affiliate_id: originalAffiliateId }
        : {}),
    };
    setTimeout(() => {
      Promise.all([fetchCoatingCourseInfos(params), fetchCoatingUnits()])
        .then(([courseRes, unitsRes]) => {
          const data = courseRes.data;
          if (!data.success) {
            if ('error_message' in data) {
              f7.dialog.alert('', data.error_message);
            }
            return;
          }
          setCourseItems(data.course_items);
          setOptionItems(data.option_items);

          if (unitsRes.data?.data?.units) {
            setCoatingUnits(unitsRes.data.data.units);
          }
        })
        .catch(() => {})
        .finally(() => {
          f7.preloader.hide();
        });
    }, 1000);

    // 顧客情報から予約者情報のデフォルト値を設定
    if (!status && !step2Data.preferredDatetimes.datetime1) {
      const [tel1 = '', tel2 = '', tel3 = ''] =
        storeCustomerInfo.phone_no1?.split('-') || [];
      setStep2Data({
        preferredDatetimes: {},
        reservationHolder: {
          familyName: storeCustomerInfo.family_name || '',
          firstName: storeCustomerInfo.first_name || '',
          familyNameKana: storeCustomerInfo.family_name_kana || '',
          firstNameKana: storeCustomerInfo.first_name_kana || '',
          tel1,
          tel2,
          tel3,
        },
        mUnitId: undefined,
        unitMemo: '',
      });
    }
  }, [courseItems, storeCustomerInfo]);

  const submitStep1 = (
    data: CoatingStep1FormData,
    fee: number,
    workingMinutes: number,
  ) => {
    setStep(coatingSteps.REGISTATION);
    setStep1Data(data);
    setTotalFee(fee);
    setTotalWorkingMinutes(workingMinutes);
  };

  const submitStep2 = (data: CoatingStep2FormData) => {
    setStep(coatingSteps.ADDITIONAL_INFO);
    setStep2Data(data);
  };

  const submitStep3 = (data: CoatingStep3FormData) => {
    setStep(coatingSteps.REVIEW);
    setStep3Data(data);
  };

  /**
   * Step4: Paidy で決済して予約作成まで一気通貫で行う
   * 1. Paidy Checkout 起動 → payment_id 取得
   * 2. paidy-confirm API → capture → t_payment_managements_id 取得
   * 3. createCoating API で予約レコード作成
   *
   * 決済が成立した後（step2 を通過）に createCoating が失敗した場合、
   * 二重決済を避けるため決済済み payload を localStorage に退避し、
   * 再操作時は Paidy Checkout をスキップして createCoating だけリトライする。
   */
  const submitStep4Paidy = async (data?: CreateCoatingParams) => {
    if (!data || submitSuccess) return;

    // 既に Paidy capture 済みで予約作成だけ失敗しているケースは Paidy を再起動しない
    const pendingRaw = localStorage.getItem('coating_paidy_pending');
    if (pendingRaw) {
      try {
        const pending = JSON.parse(pendingRaw) as {
          createData: CreateCoatingParams;
          totalFee: number;
        };
        if (pending?.createData?.t_payment_managements_id) {
          // 主要項目（決済金額に影響する選択）が変わっていないかチェック
          const sameOrder =
            pending.createData.coating_item_code === data.coating_item_code &&
            pending.createData.body_size === data.body_size &&
            pending.createData.polish_option === data.polish_option &&
            JSON.stringify(pending.createData.option_item_codes ?? []) ===
              JSON.stringify(data.option_item_codes ?? []) &&
            pending.totalFee === totalFee;

          if (sameOrder) {
            // 同じ注文内容 → 最新の入力（氏名や日時の微修正）を反映しつつ
            // 決済済みの t_payment_managements_id だけ流用して予約作成だけリトライ
            executeCreateCoating({
              ...data,
              t_payment_managements_id:
                pending.createData.t_payment_managements_id,
            });
            return;
          }

          // 注文内容が変わっている → Paidy 再起動による二重課金を避けるため停止
          f7.dialog.alert(
            '前回の決済が完了していますが、選択内容が変わったため自動的に予約を作成できません。お手数ですがサポートまでご連絡ください。',
            '決済処理中の予約があります',
          );
          return;
        }
      } catch {
        // パース失敗時はキャッシュを破棄して通常フローに進む
        localStorage.removeItem('coating_paidy_pending');
      }
    }

    f7.preloader.show();
    try {
      // 1. Paidy Checkout 起動
      const checkoutRes = await launchPaidyCheckout({
        mode: 'payment',
        amount: totalFee,
        orderRef: `coating_${Date.now()}`,
        itemTitle: 'ボディコーティング',
      });

      if (!checkoutRes.success || !checkoutRes.paymentId) {
        // ユーザーが Paidy 画面を閉じた／認可失敗
        f7.preloader.hide();
        return;
      }

      // 2. capture + t_payment_managements 保存
      const { data: confirmRes } = await paidyConfirmCoating({
        m_customer_id: store.state.authInfo.m_customer_id,
        m_affiliate_id: store.state.customerInfo.m_affiliate_id,
        amount: totalFee,
        coating_item_code: data.coating_item_code,
        body_size: data.body_size,
        polish_option: data.polish_option,
        paidy_payment_id: checkoutRes.paymentId,
      });

      if (!confirmRes.success) {
        if ('error_message' in confirmRes) {
          f7.dialog.alert('', confirmRes.error_message);
        } else {
          f7.dialog.alert('', 'Paidy決済の確定に失敗しました');
        }
        f7.preloader.hide();
        return;
      }

      // 3. capture 成功 → 予約作成 payload を退避してから createCoating
      const createData: CreateCoatingParams = {
        ...data,
        t_payment_managements_id: confirmRes.data.t_payment_managements_id,
      };
      localStorage.setItem(
        'coating_paidy_pending',
        JSON.stringify({ createData, totalFee }),
      );
      executeCreateCoating(createData);
    } catch (error) {
      console.error('[Paidy coating]', error);
      f7.dialog.alert('', 'Paidy決済処理でエラーが発生しました');
      f7.preloader.hide();
    }
  };

  /**
   * Step4: 決済に進む
   * 1. preparePayment API → order_id 取得
   * 2. GMO URL生成 API → LinkUrl 取得
   * 3. ステップデータをlocalStorageに保存
   * 4. GMO URLにリダイレクト
   */
  const submitStep4 = async (data?: CreateCoatingParams) => {
    if (!data || submitSuccess) return;

    f7.preloader.show();
    try {
      // 1. 決済準備API
      const { data: prepareRes } = await prepareCoatingPayment({
        m_customer_id: store.state.authInfo.m_customer_id,
        m_affiliate_id: store.state.customerInfo.m_affiliate_id,
        amount: totalFee,
        coating_item_code: data.coating_item_code,
        body_size: data.body_size,
        polish_option: data.polish_option,
      });

      if (!prepareRes.success) {
        if ('error_message' in prepareRes) {
          f7.dialog.alert('', prepareRes.error_message);
        }
        f7.preloader.hide();
        return;
      }

      const { order_id, t_payment_managements_id, amount } = prepareRes.data;

      // 2. GMO決済URL生成
      const memberIdStr = `C${store.state.authInfo.m_customer_id}_${store.state.customerInfo.m_affiliate_id}`;
      const gmoParams: GmoPaymentUrlGenerationParams = {
        m_affiliate_id: store.state.customerInfo.m_affiliate_id.toString(),
        m_customer_id: store.state.authInfo.m_customer_id.toString(),
        config_id: '1',
        order_id: order_id,
        amount: amount.toString(),
        complete_url: `${endpoint.BASE_URL}${paths.coatingStatus(
          'gmo_success',
        )}`,
        cancel_url: `${endpoint.BASE_URL}${paths.coatingStatus('gmo_cancel')}`,
        member_id: memberIdStr,
      };

      const gmoRes = await postCreateGmoLinkTypePlusCardParameterApi(gmoParams);
      if (!gmoRes.data.success || !('LinkUrl' in gmoRes.data.data)) {
        f7.dialog.alert('', '決済URLの生成に失敗しました');
        f7.preloader.hide();
        return;
      }

      // 3. ステップデータをlocalStorageに保存
      paymentStorage.setItem(
        'coating_t_payment_managements_id',
        t_payment_managements_id.toString(),
      );
      paymentStorage.setItem('coating_step_1', JSON.stringify(step1Data));
      paymentStorage.setItem('coating_step_2', JSON.stringify(step2Data));
      paymentStorage.setItem('coating_step_3', JSON.stringify(step3Data));
      paymentStorage.setItem('coating_create_data', JSON.stringify(data));
      paymentStorage.setItem('coating_total_fee', totalFee.toString());
      paymentStorage.setItem(
        'coating_total_working_minutes',
        totalWorkingMinutes.toString(),
      );
      persistGmoLoginContext({
        token: store.state.authInfo.token,
        customerId: store.state.authInfo.m_customer_id,
        loginInfo: store.state.loginInfo,
      });

      // 4. GMO決済画面にリダイレクト
      window.location.href = gmoRes.data.data.LinkUrl;
    } catch (error) {
      f7.dialog.alert('', '決済処理でエラーが発生しました');
      f7.preloader.hide();
    }
  };

  /**
   * 支払い方法に応じて Step4 の確定処理を振り分ける
   */
  const submitStep4WithMethod = (
    method: CoatingPaymentMethod,
    data?: CreateCoatingParams,
  ) => {
    if (method === coatingPaymentMethod.PAIDY) {
      submitStep4Paidy(data);
      return;
    }
    submitStep4(data);
  };

  return {
    step,
    step1Data,
    step2Data,
    step3Data,
    courseItems,
    optionItems,
    coatingUnits,
    totalFee,
    totalWorkingMinutes,
    isGmoProcessing,
    customerEmail: storeCustomerInfo?.email,
    setStep,
    submitStep1,
    submitStep2,
    submitStep3,
    submitStep4,
    submitStep4WithMethod,
    retryCreateCoating,
  };
};

function clearCoatingStorage() {
  paymentStorage.removeItem('coating_t_payment_managements_id');
  paymentStorage.removeItem('coating_step_1');
  paymentStorage.removeItem('coating_step_2');
  paymentStorage.removeItem('coating_step_3');
  paymentStorage.removeItem('coating_create_data');
  paymentStorage.removeItem('coating_total_fee');
  paymentStorage.removeItem('coating_total_working_minutes');
  localStorage.removeItem('coating_paidy_pending');
}
